import React from 'react';
import CommonTabSwitch from '../CommonTabSwitch/CommonTabSwitch';

/** One tab. `eventKey` selects it, `eventName` is the label i18n resolves. */
export interface ProductCostingTabMenuItem {
  eventKey?: string | undefined;
  eventName?: string | undefined;
  isDisabled?: boolean | undefined;
  [key: string]: unknown;
}

export interface ProductCostingTabMenuProps {
  activeTheTab: (eventKey: string) => void;
  masterActiveTab?: string | null | undefined;
  MenuList?: ProductCostingTabMenuItem[] | undefined;
  isAPILoading?: boolean;
  underlineClassName?: string;
  /**
   * Accepted so existing callers keep compiling; the shared component has no use for them.
   * `loader` / `loader2` gated the old resize timer, and `isNormalSwitch` chose between the
   * greedy-nav bar and a plain pill row that had no overflow handling at all — both are now the
   * same component, which is the point of the change.
   */
  loader?: boolean | undefined;
  loader2?: boolean | undefined;
  isNormalSwitch?: boolean;
}

/**
 * Costing template tabs. A thin adapter over the shared `CommonTabSwitch` so every caller picks up
 * the segmented pill design (Figma 12358:160579 / 12948:715059) without changing its own props.
 *
 * `CommonTabSwitch` measures the tabs against the container and shows as many as fit end to end,
 * collapsing the remainder behind the MoreIcon — which replaces the previous `greedy-nav`, whose
 * overflow worked by moving `<li>` nodes between a visible and a hidden list on a 300ms timer and
 * re-entering itself until the row fitted. Measurement now happens in a hidden mirror list on a
 * ResizeObserver, so the row never paints mid-reflow.
 *
 * Labels are also no longer truncated at 20 characters by `shortenandHoverString`: overflow is a
 * width problem the MoreIcon now solves, so a long template name reads in full rather than elided.
 *
 * Shared by Product Costing (both modules), the Costing TODO list and the Range Plan product list.
 */
function ProductCostingTabMenu({
  activeTheTab,
  masterActiveTab,
  MenuList,
  isAPILoading = false,
  underlineClassName = '',
}: ProductCostingTabMenuProps): React.JSX.Element {
  return (
    <CommonTabSwitch
      masterActiveTab={masterActiveTab ?? ''}
      MenuList={(MenuList ?? []).map((item) => ({
        eventKey: item.eventKey ?? '',
        eventName: item.eventName ?? '',
        ...(item.isDisabled === undefined ? {} : { isDisabled: item.isDisabled }),
      }))}
      isAPILoading={isAPILoading}
      activeTheTab={activeTheTab}
      underlineClassName={underlineClassName}
    />
  );
}

export default ProductCostingTabMenu;
